import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  type DaycareCapacityRow,
  deleteDaycareCapacity,
  listDaycareCapacity,
  upsertDaycareCapacity,
} from '@/lib/api';
import { Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Sub-spec 04 Plan 5 M1 — Daycare capacity settings UI.
//
// Per-facility weekly grid: 7 rows (Mon→Sun) × 3 columns (full_day,
// half_day_am, half_day_pm). Each cell shows the current maxDogs cap (or
// "—" if uncapped) and opens an editor modal on click.
//
// Optimistic UI is intentionally NOT used here — capacity changes are rare
// (operator opens the page, sets caps once, closes) and a re-list keeps the
// row IDs in sync with the server after upsert/delete.
// ---------------------------------------------------------------------------

const DAYS = [
  { dow: 1, label: 'Mon' },
  { dow: 2, label: 'Tue' },
  { dow: 3, label: 'Wed' },
  { dow: 4, label: 'Thu' },
  { dow: 5, label: 'Fri' },
  { dow: 6, label: 'Sat' },
  { dow: 0, label: 'Sun' },
] as const;

const SESSION_TYPES = [
  { value: 'full_day', label: 'Full day' },
  { value: 'half_day_am', label: 'Half AM' },
  { value: 'half_day_pm', label: 'Half PM' },
] as const;

type SessionTypeValue = (typeof SESSION_TYPES)[number]['value'];

interface CellLookup {
  // Keyed by `${dow}|${sessionType}` for fast cell render.
  [key: string]: DaycareCapacityRow;
}

function cellKey(dow: number, st: string): string {
  return `${dow}|${st}`;
}

function buildLookup(rows: DaycareCapacityRow[], facilityId: string): CellLookup {
  const out: CellLookup = {};
  for (const r of rows) {
    if (r.facilityId !== facilityId) continue;
    if (r.dayOfWeek === null) continue;
    out[cellKey(r.dayOfWeek, r.sessionType)] = r;
  }
  return out;
}

interface EditState {
  row: DaycareCapacityRow | null;
  facilityId: string;
  dow: number;
  sessionType: SessionTypeValue;
}

function parseMaxBySize(json: string | null): Record<string, number> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'number' && Number.isInteger(v)) out[k] = v;
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

function CellEditor({
  edit,
  onClose,
  onSaved,
}: {
  edit: EditState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existingMaxBySize = parseMaxBySize(edit.row?.maxBySize ?? null);
  const [maxDogs, setMaxDogs] = useState<string>(edit.row ? String(edit.row.maxDogs) : '10');
  const [small, setSmall] = useState<string>(
    existingMaxBySize.small !== undefined ? String(existingMaxBySize.small) : '',
  );
  const [medium, setMedium] = useState<string>(
    existingMaxBySize.medium !== undefined ? String(existingMaxBySize.medium) : '',
  );
  const [large, setLarge] = useState<string>(
    existingMaxBySize.large !== undefined ? String(existingMaxBySize.large) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const parsedMax = Number(maxDogs);
    if (!Number.isFinite(parsedMax) || parsedMax < 0) {
      setError('Max dogs must be a non-negative number.');
      return;
    }
    const maxBySize: Record<string, number> = {};
    for (const [k, v] of [
      ['small', small],
      ['medium', medium],
      ['large', large],
    ] as const) {
      if (v.trim() !== '') {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
          setError(`Per-size cap for ${k} must be a non-negative integer.`);
          return;
        }
        maxBySize[k] = n;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await upsertDaycareCapacity({
        facilityId: edit.facilityId,
        dayOfWeek: edit.dow,
        sessionType: edit.sessionType,
        maxDogs: Math.floor(parsedMax),
        maxBySize: Object.keys(maxBySize).length > 0 ? maxBySize : null,
      });
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.issues
          ? (err.body.issues as string[]).join('; ')
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!edit.row) return;
    setSaving(true);
    setError(null);
    try {
      await deleteDaycareCapacity(edit.row.id);
      onSaved();
    } catch {
      setError('Could not remove this cap. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">
            {DAYS.find((d) => d.dow === edit.dow)?.label} ·{' '}
            {SESSION_TYPES.find((s) => s.value === edit.sessionType)?.label}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>
        <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <div>
            <label
              htmlFor="cap-max-dogs"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Max dogs (total)
            </label>
            <input
              id="cap-max-dogs"
              type="number"
              min={0}
              value={maxDogs}
              onChange={(e) => setMaxDogs(e.target.value)}
              disabled={saving}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <fieldset className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <legend className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Per-size sub-caps (optional)
            </legend>
            {[
              { id: 'cap-small', label: 'Small', value: small, set: setSmall },
              { id: 'cap-medium', label: 'Medium', value: medium, set: setMedium },
              { id: 'cap-large', label: 'Large', value: large, set: setLarge },
            ].map((s) => (
              <div key={s.id}>
                <label
                  htmlFor={s.id}
                  className="mb-1 block text-[10px] font-medium text-muted-foreground"
                >
                  {s.label}
                </label>
                <input
                  id={s.id}
                  type="number"
                  min={0}
                  value={s.value}
                  onChange={(e) => s.set(e.target.value)}
                  disabled={saving}
                  placeholder="—"
                  className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            ))}
          </fieldset>
          <div className="flex items-center justify-between gap-2 pt-2">
            {edit.row ? (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={saving}
                className="flex items-center gap-1 rounded-md border border-destructive/30 px-3 py-1.5 text-sm text-destructive press"
                data-testid="cap-delete"
              >
                <Trash2 className="size-3.5" strokeWidth={1.75} />
                Remove cap
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-md border border-border px-3 py-1.5 text-sm press"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground press"
                data-testid="cap-save"
              >
                {saving ? 'Saving…' : edit.row ? 'Save' : 'Add cap'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export function DaycareCapacity() {
  const { state } = useAuthContext();
  const facilities = state.me?.facilitiesInScope ?? [];
  const defaultFacilityId = state.me?.activeFacility?.id ?? facilities[0]?.id ?? '';
  const [facilityId, setFacilityId] = useState<string>('');
  const [rows, setRows] = useState<DaycareCapacityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [edit, setEdit] = useState<EditState | null>(null);

  useEffect(() => {
    if (defaultFacilityId && !facilityId) setFacilityId(defaultFacilityId);
  }, [defaultFacilityId, facilityId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const { rows: r } = await listDaycareCapacity();
      setRows(r);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setPermissionDenied(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const lookup = useMemo(() => buildLookup(rows, facilityId), [rows, facilityId]);

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage daycare capacity
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Daycare capacity</h1>
          <p className="mt-2 max-w-prose text-sm text-muted-foreground">
            Set the maximum number of dogs allowed per session type, per day of week. Add optional
            per-size sub-caps to limit specific dog sizes (e.g. small dogs only on half-day-AM).
          </p>
        </div>
      </header>

      {facilities.length > 1 && (
        <div className="mb-6">
          <label
            htmlFor="cap-facility"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Facility
          </label>
          <select
            id="cap-facility"
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
          <div className="h-64 w-full animate-pulse rounded bg-secondary" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-surface ring-1 ring-border elevate">
          <table className="min-w-[560px] w-full text-sm" data-testid="capacity-grid">
            <thead className="bg-secondary/40">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Day
                </th>
                {SESSION_TYPES.map((s) => (
                  <th
                    scope="col"
                    key={s.value}
                    className="px-3 py-2 text-left text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((d) => (
                <tr key={d.dow} className="border-t border-border">
                  <th scope="row" className="px-3 py-3 text-left font-medium">
                    {d.label}
                  </th>
                  {SESSION_TYPES.map((s) => {
                    const row = lookup[cellKey(d.dow, s.value)];
                    return (
                      <td key={s.value} className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            setEdit({
                              row: row ?? null,
                              facilityId,
                              dow: d.dow,
                              sessionType: s.value,
                            })
                          }
                          disabled={!facilityId}
                          className="flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-1.5 text-left text-sm press"
                          data-testid={`cell-${d.dow}-${s.value}`}
                        >
                          {row ? (
                            <span className="num font-mono">{row.maxDogs}</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Plus className="size-3" strokeWidth={1.75} />
                              Add cap
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <CellEditor
          edit={edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
